/**
 * CopyableAmount - Component for displaying dual-currency amounts with hover-to-switch
 *
 * Displays OLV by default, switches to USD on hover, and allows click-to-copy
 * of the currently displayed value.
 *
 * @example
 * ```tsx
 * <CopyableAmount
 *   olvValue={1234.56}
 *   usdValue={1.23}
 *   showPositive={true}
 * />
 * ```
 */

import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import type { CopyableAmountProps } from './types';
import {
  AmountContainer,
  AmountDisplay,
  AmountValue,
  AmountLabel,
  DualValueTooltip,
  CurrencyRow,
  CurrencyLabel,
  CurrencyValue,
  CopyIconButton,
  TooltipDivider,
  TooltipInstruction,
} from './styles';
import { formatOlv, formatUsd, formatWu, getCurrencyLabel } from './utils';
import { useTooltipPosition, copyToClipboard } from '../CopyableValue/utils';
import { PortalTooltipBubble, TooltipRow } from '../CopyableValue/styles';

/**
 * CopyableAmount Component
 */
export const CopyableAmount: React.FC<CopyableAmountProps> = ({
  olvValue,
  usdValue,
  unitCurrency = 'olv',
  showPositive = false,
  defaultCurrency = 'olv',
  colorOverride,
  onCopy,
  className,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isTooltipHovered, setIsTooltipHovered] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedCurrency, setCopiedCurrency] = useState<'olv' | 'usd'>('olv');
  const [copiedValue, setCopiedValue] = useState<string>('');
  const triggerRef = useRef<HTMLDivElement>(null);
  const valueRef = useRef<HTMLSpanElement>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Format values (the unit slot formats as WU or legacy OLV per unitCurrency)
  const formattedOlv = useMemo(
    () => (unitCurrency === 'wu' ? formatWu(olvValue, showPositive) : formatOlv(olvValue, showPositive)),
    [olvValue, showPositive, unitCurrency]
  );
  const formattedUsd = useMemo(() => formatUsd(usdValue, showPositive), [usdValue, showPositive]);
  const unitLabel = getCurrencyLabel(unitCurrency);

  // Determine which currency to display
  const displayCurrency = isHovered ? 'usd' : defaultCurrency;
  const displayValue = displayCurrency === 'usd' ? formattedUsd : formattedOlv;
  const displayLabel = displayCurrency === 'usd' ? getCurrencyLabel('usd') : unitLabel;

  // Tooltip should be visible if either trigger or tooltip is hovered
  const shouldShowTooltip = (isHovered || isTooltipHovered) && !copied;

  // Tooltip positioning - use valueRef to position relative to the actual text
  // Balanced gaps: 45px above (clears value + reachable), 15px below (perfect)
  const mainTooltipPosition = useTooltipPosition(valueRef, shouldShowTooltip, 220, 100, 45, 15);
  const successTooltipPosition = useTooltipPosition(valueRef, copied, 120, 40, 45, 15);

  // Handle main click (copy currently displayed value)
  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();

    const currency = displayCurrency;
    const value = displayValue;

    const success = await copyToClipboard(value);
    if (success) {
      setCopiedCurrency(currency);
      setCopiedValue(value);
      setCopied(true);
      onCopy?.(value, currency);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  // Handle individual currency copy from tooltip
  const handleCopyCurrency = async (currency: 'olv' | 'usd', e: React.MouseEvent) => {
    e.stopPropagation();

    const value = currency === 'olv' ? formattedOlv : formattedUsd;

    const success = await copyToClipboard(value);
    if (success) {
      setCopiedCurrency(currency);
      setCopiedValue(value);
      setCopied(true);
      onCopy?.(value, currency);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  // Clear any pending hide timeout
  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  // Handle mouse enter on trigger
  const handleTriggerEnter = useCallback(() => {
    clearHideTimeout();
    setIsHovered(true);
  }, [clearHideTimeout]);

  // Handle mouse leave on trigger (start delay before hiding)
  const handleTriggerLeave = useCallback(() => {
    setIsHovered(false);
    // Start a timeout to hide the tooltip, giving user time to move to tooltip
    // If user moves to tooltip before this fires, it will be cancelled
    hideTimeoutRef.current = setTimeout(() => {
      setIsTooltipHovered(false);
    }, 150); // 150ms delay for smooth transition
  }, []);

  // Handle mouse enter on tooltip
  const handleTooltipEnter = useCallback(() => {
    clearHideTimeout();
    setIsTooltipHovered(true);
  }, [clearHideTimeout]);

  // Handle mouse leave on tooltip
  const handleTooltipLeave = useCallback(() => {
    setIsTooltipHovered(false);
    // Also ensure trigger hover is false
    setIsHovered(false);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      clearHideTimeout();
    };
  }, [clearHideTimeout]);

  return (
    <AmountContainer
      ref={triggerRef}
      $hovered={isHovered}
      $copied={copied}
      onMouseEnter={handleTriggerEnter}
      onMouseLeave={handleTriggerLeave}
      onClick={handleCopy}
      className={className}
      role="button"
      aria-label={`Amount: ${formattedOlv} ${unitLabel}, ${formattedUsd} USD. Click to copy.`}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCopy(e as any);
        }
      }}
    >
      <AmountDisplay>
        <AmountValue ref={valueRef} $positive={showPositive && olvValue > 0} $colorOverride={colorOverride}>
          {displayValue}
        </AmountValue>
        <AmountLabel>{displayLabel}</AmountLabel>
      </AmountDisplay>

      {/* Main tooltip showing both currencies */}
      {ReactDOM.createPortal(
        <PortalTooltipBubble
          $visible={shouldShowTooltip}
          $position={mainTooltipPosition}
          $variant="default"
          $maxWidth={220}
          onMouseEnter={handleTooltipEnter}
          onMouseLeave={handleTooltipLeave}
        >
          <DualValueTooltip>
            <div style={{ fontSize: '0.6rem', color: '#64748b', fontWeight: 600, marginBottom: '0.25rem' }}>
              amount
            </div>

            <CurrencyRow>
              <CurrencyLabel>{unitLabel}</CurrencyLabel>
              <CurrencyValue>{formattedOlv}</CurrencyValue>
              <CopyIconButton onClick={(e) => handleCopyCurrency('olv', e)} title={`Copy ${unitLabel} value`}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <rect x="9" y="9" width="13" height="13" rx="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
              </CopyIconButton>
            </CurrencyRow>

            <CurrencyRow>
              <CurrencyLabel>{getCurrencyLabel('usd')}</CurrencyLabel>
              <CurrencyValue>{formattedUsd}</CurrencyValue>
              <CopyIconButton onClick={(e) => handleCopyCurrency('usd', e)} title="Copy usd value">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <rect x="9" y="9" width="13" height="13" rx="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
              </CopyIconButton>
            </CurrencyRow>
          </DualValueTooltip>
        </PortalTooltipBubble>,
        document.body
      )}

      {/* Success tooltip */}
      {ReactDOM.createPortal(
        <PortalTooltipBubble
          $visible={copied}
          $position={successTooltipPosition}
          $variant="success"
          $maxWidth={120}
        >
          <TooltipRow $justify="center">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" style={{ width: 12, height: 12 }}>
              <polyline points="20 6 9 17 4 12" />
            </svg>
            {copiedValue} {copiedCurrency === 'usd' ? getCurrencyLabel('usd') : unitLabel} copied!
          </TooltipRow>
        </PortalTooltipBubble>,
        document.body
      )}
    </AmountContainer>
  );
};

export default CopyableAmount;
